import React from 'react';
import { Home } from 'lucide-react';
import PVSPageLayout from '../PVSPageLayout';
import EmployeeAcknowledgment from '../EmployeeAcknowledgment';
import DocumentArchiveButton from '../DocumentArchiveButton';

const DEFAULT_META = [
  ['Company', 'Pressure Vessel Services Limited'],
  ['Date of Assessment', '17th March 2026'],
  ['Prepared by', 'Pressure Vessel Services Limited'],
  ['Approved by', 'Board of Directors'],
  ['Review Period', '24 months'],
];

const DocumentPage = ({
  pageTitle,
  documentHeading,
  meta = [],
  includeDefaultMeta = true,
  children,
  acknowledgment,
  onReturnHome,
}) => {
  const mergedMeta = includeDefaultMeta
    ? [...DEFAULT_META.filter(([label]) => !meta.find(([l]) => l === label)), ...meta]
    : meta;

  return (
    <PVSPageLayout className="py-12 px-4">
      <div className="max-w-4xl mx-auto pvs-glass border border-pvs-accent/30 rounded-xl shadow-pvs-glow">
        <div className="border-b border-pvs-border p-6">
          <div className="flex justify-between items-center gap-4">
            <h1 className="text-2xl sm:text-3xl font-bold text-white flex-1 min-w-0">{pageTitle}</h1>
            <DocumentArchiveButton />
            <button
              className="group flex items-center justify-center w-12 h-12 flex-shrink-0 rounded-xl pvs-glass pvs-glass-hover transition-all duration-200"
              onClick={onReturnHome}
              title="Return to Homepage"
            >
              <Home className="w-6 h-6" stroke="url(#icon-two-tone)" />
            </button>
          </div>
        </div>
        <div className="p-8 md:p-10 lg:p-12">
          <h2 className="text-2xl font-bold text-center mb-6 text-white">{documentHeading}</h2>
          <table className="w-full border-collapse mb-8" style={{ borderColor: '#0437F2' }}>
            <tbody>
              {mergedMeta.map(([label, value]) => (
                <tr key={label}>
                  <th
                    className="text-white text-left py-2 px-4 border font-semibold w-1/3"
                    style={{ backgroundColor: '#0437F2', borderColor: '#0437F2' }}
                  >
                    {label}
                  </th>
                  <td
                    className="py-2 px-4 border text-pvs-text-muted"
                    style={{ borderColor: '#0437F2' }}
                  >
                    {value}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {children}
          {acknowledgment && (
            <EmployeeAcknowledgment
              documentName={acknowledgment.documentName}
              documentReference={acknowledgment.documentReference || ''}
              processActivity={acknowledgment.processActivity}
              versionNumber={acknowledgment.versionNumber || ''}
              dateOfIssue={acknowledgment.dateOfIssue || ''}
            />
          )}
        </div>
      </div>
    </PVSPageLayout>
  );
};

export const Section = ({ number, title, children }) => (
  <section className="mb-8">
    <h3 className="text-2xl font-bold mb-4 text-pvs-accent">
      {number ? `${number}. ${title}` : title}
    </h3>
    {children}
  </section>
);

export const SubSection = ({ title, children }) => (
  <>
    <h4 className="text-xl font-semibold mb-3 mt-6 text-pvs-accent">{title}</h4>
    {children}
  </>
);

export const Paragraph = ({ children, className = '' }) => (
  <p className={`text-pvs-text-muted ${className}`}>{children}</p>
);

export const BulletList = ({ items, className = '' }) => (
  <ul className={`list-disc pl-5 space-y-2 text-pvs-text-muted ${className}`}>
    {items.map((item, i) => (
      <li key={i}>{item}</li>
    ))}
  </ul>
);

export const OrderedList = ({ items, className = '' }) => (
  <ol className={`list-decimal pl-6 space-y-2 text-pvs-text-muted ${className}`}>
    {items.map((item, i) => (
      <li key={i}>{item}</li>
    ))}
  </ol>
);

export const DoDoNot = ({ doItems = [], doNotItems = [] }) => (
  <div className="grid md:grid-cols-2 gap-6">
    {doItems.length > 0 && (
      <div className="pvs-glass p-4 rounded-lg border border-emerald-500/30">
        <h5 className="text-lg font-bold mb-3 text-emerald-400">Do</h5>
        <ul className="list-disc pl-5 space-y-2 text-pvs-text-muted">
          {doItems.map((item, i) => (
            <li key={i}>{item}</li>
          ))}
        </ul>
      </div>
    )}
    {doNotItems.length > 0 && (
      <div className="pvs-glass p-4 rounded-lg border border-red-500/30">
        <h5 className="text-lg font-bold mb-3 text-red-400">Do Not</h5>
        <ul className="list-disc pl-5 space-y-2 text-pvs-text-muted">
          {doNotItems.map((item, i) => (
            <li key={i}>{item}</li>
          ))}
        </ul>
      </div>
    )}
  </div>
);

export const HazardTable = ({ rows, columns = ['Hazard', 'Control Measures Already in Place', 'Additional Control Measures Required'] }) => (
  <div className="overflow-x-auto rounded-lg shadow-md">
    <table className="min-w-full">
      <thead>
        <tr className="bg-pvs-card-light">
          {columns.map((col) => (
            <th key={col} className="py-3 px-4 uppercase font-semibold text-sm text-pvs-text-muted text-left">
              {col}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map((row, i) => (
          <tr key={i} className={i % 2 === 0 ? 'bg-white/5' : ''}>
            {row.map((cell, j) => (
              <td key={j} className="py-3 px-4 text-pvs-text-muted align-top">
                {Array.isArray(cell) ? (
                  <ul className="list-disc pl-5 space-y-1">
                    {cell.map((bullet, k) => (
                      <li key={k}>{bullet}</li>
                    ))}
                  </ul>
                ) : (
                  cell
                )}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

export const Callout = ({ children, tone = 'default' }) => {
  const toneClass =
    tone === 'warning'
      ? 'border-amber-500/40 bg-amber-500/10'
      : tone === 'danger'
      ? 'border-red-500/40 bg-red-500/10'
      : 'border-pvs-accent/40 bg-pvs-accent/5';
  return (
    <div className={`border rounded-lg p-4 my-4 text-pvs-text-muted ${toneClass}`}>{children}</div>
  );
};

export default DocumentPage;
